<template>
  <ion-page id="builder-page">  

    <AlertReplacement :alertInfo="alertInfo" :trigger="alertTrigger"
              @alert-result="alertResult"></AlertReplacement>

    <ion-content>

      <div id="the_board" :style="styleBoard">
        <CMChessboard :fen="fen" :validMoves="availableMoves" :orientation="orientation" 
                    :boardSize="boardSize" @board-move="boardMove" />
      </div>

      <div id="the_slice_movelist"
                :class="{ 'text-dark' : isDark,'text-light' : !isDark }" :style="styleMoveList">
        <MoveListSelector :resultsData="availableMoves" :eighty="eighty"
            :moveNumber="moveNumberValue"
            :showCheckBoxes="whatToDisplay == DISPLAY_GRAPH"
            :moveListTrigger="availableMovesTrigger" 
            @checkbox-click="moveListCheckboxClick" 
            @move-click="moveListMoveClick" />
      </div>

      <div id="the_slice_chart"
                :class="{ 'text-dark' : isDark,'text-light' : !isDark }" :style="styleChart">

        <div v-show="whatToDisplay == DISPLAY_GRAPH">
          <SliceDiceAgChart :moveList="availableMoves" :moveNumber="moveNumberValue" 
                :showAdvantage="showWhiteAdvantage" :moveListTrigger="availableMovesTrigger" 
                :styleHeight="styleChartAgHeight"/> 

          <div id="the_slider"  >
            <ion-list>
              <ion-item> 
                <!-- move number for bar chart -->
                <ion-button size="small" @click="toggleWhiteAdvantage()">
                  {{ whiteAdvantageLabel }} @{{ t("Move") }} {{ moveNumberValue }} 
                </ion-button>
                <ion-range aria-label="Range with ticks" :ticks="true"    
                          :snaps="true" v-model="moveNumberValue"
                          :min="12" :max="21" :step="3" class="move_slider_style"
                          @ionChange="moveNumberChange">
                </ion-range>
              </ion-item>
            </ion-list>
          </div>
        </div>

        <div v-show="whatToDisplay == DISPLAY_TREE">
          <RepTreeNewView 
                :treeDisplay="treeDisplay"
                :treeTrigger="treeTrigger" 
                @move-click="repTreeMoveClick"
                @branch-click="repTreeBranchClick"/> 

        </div>
      </div>

      <div id="the_movehistory" :style="styleMoveHistory" 
                    :class="{ 'text-dark' : isDark, 'text-light' : !isDark }">
        <span v-if="moveHistoryStack.length == 0">
          <b>{{ t("Builder") }}: {{ repertoireDescription }} </b>
        </span>
        <MoveHistory :moveHistoryStack="moveHistoryStack" :trigger="moveHistoryTrigger" />
      </div>

    </ion-content>
    <Toolbar :elements="toolbarElements" :trigger="toolbarTrigger"        
                          @toolbar-click="toolbarClick"/>
  </ion-page>
</template>

// https://forum.ionicframework.com/t/solved-make-ion-range-align-with-an-other-element/99984

<script setup>

  import { IonItem, IonList, IonButton, IonRange,IonPage, IonContent, modalController } from '@ionic/vue';
  import { t } from "@/composables/translateText.js";

  import Toolbar from '@/components/Toolbar.vue';
  import ModalErrorMessage from '@/components/ModalErrorMessage.vue'
  import AlertReplacement from '@/components/AlertReplacement.vue'

  import MoveListSelector from '@/components/MoveListSelector.vue'
  import SliceDiceAgChart from '@/components/SliceDiceAgChart.vue'
  import RepTreeNewView from '@/components/RepTreeNewView.vue'
  import CMChessboard from '@/components/CMChessboard.vue'
  import MoveHistory from '@/components/MoveHistory.vue'

  import { ref, watch } from 'vue'
  import { findToolbarElement, setToolbarElementDisabledFlag, setToolbarElementLabel} 
                from '@/composables/toolbarSupport.js'
  import { useGetData } from '@/composables/getData.js'
  import { useConnectionStore } from '@/stores/connectionStore';
  import { onMounted, onBeforeUnmount } from 'vue'

  import { doStartupCheck } from '@/composables/startupCheck.js'
  import { availableWidthHeight, calculateChessBoardRect, isScreenPortraitOrientation } 
              from '@/composables/screenSize'
  import { useWindowSizeStore } from '@/stores/windowSizeStore';

  const TOOLBAR_BACK = 0
  const TOOLBAR_REWIND = 1
  const TOOLBAR_DISPLAY_TOGGLE = 2
  const TOOLBAR_COPY = 3
  const TOOLBAR_COMMENT = 4

  const INITIAL_FEN = "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1"

  const MOVEHISTORY_FONTSIZE = 16
  const MOVEHISTORY_HEIGHT =  MOVEHISTORY_FONTSIZE * 1.2 * 2 + 4

  const emit = defineEmits(['boardMove'])

  const connectionStore = useConnectionStore()
  var explorerType = connectionStore.getProductOption

  const isDark = ref(connectionStore.getDarkmode != "light")
  const menuTrigger = ref(0)
  const tourTrigger = ref(0)
  const showTour = ref(false)

  const DISPLAY_GRAPH = 1
  const DISPLAY_TREE = 2
  const whatToDisplay = ref(DISPLAY_GRAPH)

  const picked = ref('Two')

  const exploreColor = ref("white")
  const repertoireDescription = ref(t("Loading Repertoire Info") + "...")

  const toolbarElements = ref([
    { index : TOOLBAR_BACK, label : t("Back") , disabled : true, collapse:false},
    { index : TOOLBAR_REWIND, label : t("Rewind") , disabled : true, collapse:false},
    { index : TOOLBAR_DISPLAY_TOGGLE, label : t("Tree") , disabled : true, collapse:false},
    { index : TOOLBAR_COPY, label : t("Copy") , disabled : true, collapse:false},
    { index : TOOLBAR_COMMENT, label : t("Comment") , disabled : true, collapse:false},
  ])

  var repertoireRatingMin = 0
  var repertoireRatingMax = 0
  var repertoireName = ""
  var repertoirePercentage = 0
  var repertoireLocked = false
  var repertoireHighestRecordIndex = 0

  const whiteAdvantageLabel = ref(t("All") + " ")
  const windowSizeStore = useWindowSizeStore()
  const windowSizeRef = ref({ width : windowSizeStore.getWindowSize['width'],
                        height : windowSizeStore.getWindowSize['height'] })
  const boardSize = ref(100)

  
  const fen = ref(INITIAL_FEN)
  const initialFenid4 = "147869767"
  const initialFenidCombined = "0~" + initialFenid4
  const eighty = ref(-2)
  const showScores = ref(true)

  var playingWhite = 1
  var repertoireId = "0"
  var initialBoardSelectedInTree = true

  var blankAppendTrigger = " "
  var orientation = ref("white")

  var repTree = new Map()
  var displayTreeMap = new Map()
  var maxSpreadsheetColumnNumber = 0
  var spreadsheetColumnDefinitions = ref([])
  var treeDisplay = ref([])

  // what comes back from the server

  // merged result from the server grabs
  const availableMoves = ref([])

  const moveHistoryStack = ref([])
  const moveHistoryTrigger = ref(0)
  const moveHistoryString = ref("")
  const toolbarTrigger = ref(0)

  const alertInfo = ref({})
  const alertTrigger = ref(0)

  const moveNumberValue = ref(15)
  const availableMovesTrigger = ref(0)
  const treeTrigger = ref(0)
  const showWhiteAdvantage = ref(false)
  const styleChartAgHeight = ref("height: 500px")
  var saveSelectedSans = []
  var selectedSansStack = []

  // End Slice & Dice

  // tour stuff

  const sliceDiceTourInfo = {
    id : "opening_slicedice",
    elements : [
      { id: "", title: "Explore", text : "Why are we deciding our opening moves based on how the game ends 40 to 60 moves later?<br><br>That's an unfair burden for the game's first few moves."},
      { id: "", title: "There Is A Better Way!", text : "Chess Opening Mastery is a game changing, data driven approach to evaluating the game's first moves.<br><br>"},
      { id: "", title: "A Window into the Middle Game", text : "Chess Opening Mastery's revolutionary approach shows how the games are progressing in the middle game.<br><br>How? For every game, the Stockfish computer engine has been used to evaluate the position before white's moves 12, 15, 18 and 21. Stockfish scores each board as having an advantage for white (positive numbers) or an advantage for black (negative numbers)." },
      { id: "", title: "Stockfish Scores", text : "You probably know that a pawn is worth 1 point, a bishop/knight is worth 3 and a rook is 5 points. Stockfish reports back a position as being the equivalent of 'up' some number of pawns.<br><br>The bar chart shows that distribution for games start with the given move sequence."},
      { id: "", title: "The Game Collection", text : "The Lichess 'rapid' games were used to build the tree. With a unique twist - only games where the players are within the same 50 point 'bucket' are included.<br><br>Why? Because even a 50 point difference between two players gives the higher rated player a 6% win advantage which swamps any advantage that can be obtained by a particular opening."},
      { id: "the_slice_movelist", title: "The Move List", text : "The move choices are sorted in order of popularity."},
      { id: "the_slice_movelist", title: "The Percentage", text : "This is the percentage of your games where you'll see each move. Obviously, when it is your move you can choose any move and therefore it's popularity is not important. (This was a real breakthrough in thinking and why white at move 1 sees 100% for every choice.)"},
      { id: "the_slice_movelist", title: "Opponent Move Percentage", text : "A large part of opening exploration, is making sure you cover your opponent's more popular moves and not waste time thinking about rarely played moves."},
      { id: "the_slice_movelist", title: "Weighted score", text : "Next to the percentage is a weighted average of the Stockfish scores at the 'future move' specified on the slider. This gives you a quick idea of the future state of the game for each current move."},
      { id: "the_slice_movelist", title: "Game Count", text : "The game count is shown so you can evaluate the validity of the future move results. I.e., extraordinary results for rarely played moves should be viewed with caution."},
      { id: "the_slice_movelist", title: "Selecting Moves", text : "Clicking the move or checking the box, will add the move to the chart."},
      { id: "the_slider", title: "Selecting the Move Number", text : "The important slider is the <b>Move Number</b>. That is the <i>future</i> move that will be used for the bar chart.<br><br>The available future moves are: 12, 15, 18, and 21."},
      { id: "the_slider", title: "White's Advantage", text : "For whatever color you're playing, you want moves where your bars are 'higher'. When you find yourself looking back and forth between the equivalent white and black bars, this button will show you the difference between the corresponding bars.<br><br>For the black player then shorter bars are better"},
      { id: "the_slice_chart", title: "The Graph", text : "The graph shows the distribution of the Stockfish engine scores for games where the checked move(s) was made.<br><br>For example, if '1.e4' is checked and the slider is at move 15 then the graph shows how well players who played 'e4' are doing in their games at move 15."},
      { id: "the_slice_chart", title: "The Bars", text : "The bars show the percentage of games in each bucket. The buckets start on the left with black having already won the game by the future move number. Next to that is the percentage of games where black has a 'huge' advantage"},
      { id: "the_slice_chart", title: "The Point Bars", text : "The next several bars break the stockfish engine scores into one point buckets.<br><br><i>(The 3 point bucket represents being up a bishop and the 1 point bucket represents being up by a pawn.)</i>"},
      { id: "the_slice_chart", title: "The Middle &amp; White's Bars", text : "The center bar shows the percentage of games that are about even.<br><br>The bars to the right are white's mirroring the black buckets from 1 point through already won games."},
      { id: "the_toolbar", title: "Options", text : "Used to set the rating range and color."},
    ]
  }

  const tourInfo = ref(sliceDiceTourInfo)

  const styleChart = ref({
    position : "absolute",
    "overflow-y" : "scroll",
    top : "0px",
    left : "100px",
    width: "100px",
    height : "100px"
  })

  const styleMoveList = ref({
    position : "absolute",
    "overflow-y" : "scroll",
    top : "0px",
    left : "100px",
    width: "100px",
    height : "100px"
  })

  const styleBoard = ref({
    position : "absolute",
    top : "0px",
    left : "100px",
    width: "100px",
    height : "100px"
  })

  const styleMoveHistory = ref({
    position : "absolute",
    "overflow-y" : "scroll",
    top : "0px",
    left : "100px",
    width: "100px",
    height : "100px",
    "font-size" : MOVEHISTORY_FONTSIZE + "px",
    "text-align" : "left",
    "line-height" : "1.2em"
  })

  const styleMoveNumberSlider = ref({
    display : "inline"
  })

  doStartupCheck(false,afterStartupCallbackRoutine)

  const openErrorModal = async (programName,errorMessage) => {
    const modal = await modalController.create({
      component: ModalErrorMessage,
      componentProps: {
        type : "commError",
        program : programName ,
        errorMessage : errorMessage
      }
    });

    modal.present();

    const { data, role } = await modal.onWillDismiss();
  };

  function afterStartupCallbackRoutine(stuffChanged,errorProgram,errorEntry) {
    if (connectionStore.getIsSubscriber != 1) {
      return
    }
    menuTrigger.value++
    updateWidthHeights()
    getRepertoireInfo()
  }

  onMounted(() => {
  })

  function resetExplorerScreen() {
    // reset to beginning
    afterStartupCallbackRoutine(true,null,null)
  }

  watch(() => connectionStore.getProductOption, () => {
    explorerType = connectionStore.getProductOption
    resetExplorerScreen()
  })

  watch(() => windowSizeStore.getWindowSize, () => {
    windowSizeRef.value = { width : windowSizeStore.getWindowSize['width'],
                      height : windowSizeStore.getWindowSize['height'] }
    updateWidthHeights()
    menuTrigger.value++
  })

  function updateStyleObjects(boundaries) {
    boardSize.value = boundaries['board'].height

    styleBoard.value.height = boundaries['board'].height + "px"
    styleBoard.value.width = boundaries['board'].width + "px"
    styleBoard.value.top = boundaries['board'].top + "px"
    styleBoard.value.left = boundaries['board'].left + "px"

    styleMoveList.value.height = boundaries['moveList'].height + "px"
    styleMoveList.value.width = boundaries['moveList'].width + "px"
    styleMoveList.value.top = boundaries['moveList'].top + "px"
    styleMoveList.value.left = boundaries['moveList'].left + "px"

    styleMoveHistory.value.height = boundaries['history'].height + "px"
    styleMoveHistory.value.width = boundaries['history'].width + "px"
    styleMoveHistory.value.top = boundaries['history'].top + "px"
    styleMoveHistory.value.left = boundaries['history'].left + "px"

    styleChart.value.height = boundaries['chart'].height + "px"
    styleChart.value.width = boundaries['chart'].width + "px"
    styleChart.value.top = boundaries['chart'].top + "px"
    styleChart.value.left = boundaries['chart'].left + "px"

    const buttonHeight = 47
    styleChartAgHeight.value = "height: " + (boundaries['chart'].height - buttonHeight) + "px"
 
    styleMoveNumberSlider.value.width =  "350px"
    styleMoveNumberSlider.value['margin-left'] =  (boundaries['chart'].width/2 - 175) + "px"
  }

  function updateWidthHeights() {
    const availableSpace = availableWidthHeight()
    const isPortrait = isScreenPortraitOrientation(availableSpace)
    const boundaries = (isPortrait) ? calculatePortraitWidthHeights(availableSpace) :
                                      calculateLandscapeWidthHeights(availableSpace)
    updateStyleObjects(boundaries)
  }

  function calculatePortraitWidthHeights(availableSpace) {

    // Vertically -> Board, move history, (move list & chart side by side), sliders
    const availableSpaceForBoard = { width : availableSpace.width ,
                height : availableSpace.height / 2.5,
                isPortrait : true }

    const boardDimensions = calculateChessBoardRect(availableSpaceForBoard,1.0)
    const boardHeight = boardDimensions.height
    const boardWidth = boardDimensions.width
    const boardPositionTop = boardDimensions.y
    const boardPositionLeft = boardDimensions.x + availableSpace.leftRightIndent + availableSpace.leftIndent

    const historyPositionTop = boardPositionTop + boardHeight
    const historyPositionLeft = availableSpace.leftRightIndent + availableSpace.leftIndent
    const historyHeight = MOVEHISTORY_HEIGHT
    const historyWidth = availableSpace.width - availableSpace.leftRightIndent * 2

    const moveListPositionTop = historyPositionTop + historyHeight
    const moveListPositionLeft = availableSpace.leftRightIndent + availableSpace.leftIndent
    const moveListHeight = availableSpace.height - moveListPositionTop - MOVEHISTORY_FONTSIZE * 1.2
    const moveListWidth = 250

    const chartPositionTop = moveListPositionTop
    const chartPositionLeft = moveListPositionLeft + moveListWidth
    const chartHeight = moveListHeight
    const chartWidth = availableSpace.width - moveListWidth

    const result = { board : {
        width : boardWidth,
        height : boardHeight,
        left : boardPositionLeft + availableSpace.leftIndent,
        top : boardPositionTop+ availableSpace.topIndent
      },
      moveList : {
        width : moveListWidth,
        height : moveListHeight,
        left : moveListPositionLeft + availableSpace.leftIndent,
        top : moveListPositionTop+ availableSpace.topIndent
      },
      history : {
        width : historyWidth,
        height : historyHeight,
        left : historyPositionLeft + availableSpace.leftIndent,
        top : historyPositionTop+ availableSpace.topIndent
      },
      chart : {
        width : chartWidth,
        height : chartHeight,
        left : chartPositionLeft + availableSpace.leftIndent,
        top : chartPositionTop+ availableSpace.topIndent
      }
    }
    return (result)
  }

  function calculateLandscapeWidthHeights(availableSpace) {

    // reserve space for the instruction lines
    const availableSpaceForBoard = { width : availableSpace.width/4,
                height : availableSpace.height * .75,
                isPortrait : false }
    const boardDimensions = calculateChessBoardRect(availableSpaceForBoard,1.0)

    const boardHeight = boardDimensions.height
    const boardWidth = boardDimensions.width
    const boardPositionTop = boardDimensions.y
    const boardPositionLeft = boardDimensions.x + availableSpace.leftRightIndent

    const moveListPositionTop = 0
    const moveListPositionLeft = boardPositionLeft + boardWidth + availableSpace.leftRightIndent
    const moveListHeight = availableSpace.height
    const moveListWidth = 250

    const chartPositionTop = 0
    const chartPositionLeft = moveListPositionLeft + moveListWidth
    const chartHeight = availableSpace.height
    const chartWidth = availableSpace.width - chartPositionLeft

    const historyPositionTop = boardPositionTop + boardHeight + 5
    const historyPositionLeft = availableSpace.leftRightIndent
    const historyHeight = availableSpace.height - historyPositionTop
    const historyWidth = boardWidth

    const result = { board : {
        width : boardWidth,
        height : boardHeight,
        left : boardPositionLeft + availableSpace.leftIndent,
        top : boardPositionTop+ availableSpace.topIndent
      },
      moveList : {
        width : moveListWidth,
        height : moveListHeight,
        left : moveListPositionLeft + availableSpace.leftIndent,
        top : moveListPositionTop+ availableSpace.topIndent
      },
      history : {
        width : historyWidth,
        height : historyHeight,
        left : historyPositionLeft + availableSpace.leftIndent,
        top : historyPositionTop+ availableSpace.topIndent
      },
      chart : {
        width : chartWidth,
        height : chartHeight,
        left : chartPositionLeft + availableSpace.leftIndent,
        top : chartPositionTop+ availableSpace.topIndent
      }
    }
    return (result)
  }

  // For Slice & Dice
  function moveListCheckboxClick(item) {
    availableMoves.value[item.index].selected = item.flag
    availableMovesTrigger.value++
  }

  function moveListMoveClick(index) {
    boardMove(index)
  }

  // End for Slice & Dice

  function incrementAppendTrigger() {
    if (blankAppendTrigger.length > 64)
      blankAppendTrigger = ""
    blankAppendTrigger += " "
  }

  function tourDone() {
    showTour.value = false
  }

  function moveNumberChange({ detail }) {
    //moveNumberValue.value = detail.value
    //console.log("move number change",detail,detail.value,moveNumberValue.value)
    availableMovesTrigger.value++
  }

  function toggleWhiteAdvantage() {
    showWhiteAdvantage.value = !showWhiteAdvantage.value
    whiteAdvantageLabel.value = (showWhiteAdvantage.value) ? t("White Adv") + ":" : t("All Buckets") + ":" 
    availableMovesTrigger.value++
  }

  function alertResult(response,task,info) {
    //console.log("Alert Result",response,task,fen.value)

    if (response == "no")
      return

    if (response == "fen") {
      const copyFen = fen.value.trim()
      var fenPieces = copyFen.split(" ")
      fenPieces[5] = 1
      const fenFixed = fenPieces.join(" ")
      //console.log("fenfixed",fenFixed)
      navigator.clipboard.writeText(fenFixed);
    }
    else {
      grabPGN()
    }
  }

  function toolbarClick(index) {

    const element = findToolbarElement(toolbarElements.value,index)
    if (element.disabled)
      return

    switch (index) {
      case TOOLBAR_COPY :
        alertInfo.value = { header : t("Copy to Clipboard") + "?" , 
                message : t("'FEN' of current board OR entire 'PGN'?"), type : "fenpgn", task : "fenpgn"  }
        alertTrigger.value++
        break
      case TOOLBAR_COMMENT :
        if (moveHistoryStack.value.length == 0)
          break
        const lastMove = moveHistoryStack.value[moveHistoryStack.value.length -1]
        var comment = prompt(t("Comment for") + ": " + lastMove.san,lastMove.comment)
        if (comment == null)
          break
        comment = comment.trim()
        if (comment == lastMove.comment)
          break
        lastMove.comment = comment
        moveHistoryTrigger.value++

        // find the move in the tree
        const treeMoves = repTree.get(lastMove.fromCombined)
        if (treeMoves != null)
          for (var j=0; j < treeMoves.length; j++) {
            const keyParts = treeMoves[j].toCombined.split("~")
            if (treeMoves[j].toCombined == lastMove.toCombined) {
              treeMoves[j].comment = comment
              break
            }
          }
        updateTreeView()
        treeTrigger.value++
        setComment(lastMove,comment)
        break
      case TOOLBAR_DISPLAY_TOGGLE :
        whatToDisplay.value = (whatToDisplay.value == DISPLAY_GRAPH) ? DISPLAY_TREE : DISPLAY_GRAPH
        setToolbarElementLabel(toolbarElements.value,TOOLBAR_DISPLAY_TOGGLE, 
                  (whatToDisplay.value == DISPLAY_GRAPH) ? t("Tree") : t("Graph"))
        toolbarTrigger.value++
        break
      case TOOLBAR_REWIND :
        fen.value = INITIAL_FEN + " "
        initialBoardSelectedInTree = true

        saveSelectedSans = (selectedSansStack.length) ? selectedSansStack[0] : []
        selectedSansStack = []
        //selectedSansStack.pop()

        moveHistoryStack.value = []
        moveHistoryTrigger.value++
        getAvailableMoves(initialFenidCombined,100)
        clearTreeDisplaySelectedFlag()
        const moveIsVisible = findMoveInTreeDisplay()
        break
      case TOOLBAR_BACK :
        if (moveHistoryStack.value.length <= 1) {
          toolbarClick(TOOLBAR_REWIND)
        }
        else {

          saveSelectedSans = selectedSansStack[selectedSansStack.length - 1]
          selectedSansStack.pop()

          moveHistoryStack.value.pop()
          moveHistoryTrigger.value++

          const oneMoveHistory = moveHistoryStack.value[moveHistoryStack.value.length - 1]
          fen.value = oneMoveHistory.fen

          getAvailableMoves(oneMoveHistory.toCombined,oneMoveHistory.movePercentage)
          clearTreeDisplaySelectedFlag()
          const moveIsVisible = findMoveInTreeDisplay()
        }
        break    
    }
  }

  function boardMove(arrayIndex) {
    const oneMove = availableMoves.value[arrayIndex]

    // if this is a player move and changing the highlighted move, should get the user to verify the change
    if (repertoireLocked == false && oneMove.highlightPlayerMove == false) {
      var hasHighlightedMove = false
      for (var i=0; i < availableMoves.value.length; i++)
        if (availableMoves.value[i].highlightPlayerMove) {
          const answer = window.confirm(t("Please verify changing YOUR move from") + " " +
          availableMoves.value[i].san + " " + t("to") + " " + oneMove.san + 
          "\n\n" + t("This is not reversable and will drop any moves following") + " " +
                          availableMoves.value[i].san)
          if (answer == false)
            return
          else
            break
        }
    }
    // save current move's sans before getting next ply
    saveSelectedSans = []
    for (var i=0; i < availableMoves.value.length; i++)
      if (availableMoves.value[i].selected)
        saveSelectedSans.push(availableMoves.value[i].san)
    selectedSansStack.push(saveSelectedSans)

    saveSelectedSans = []

    fen.value = oneMove.fen

    const oneMoveHistory = {fen : fen.value, 
                          fromCombined : oneMove.fromCombined,
                          toCombined : oneMove.toCombined, 
                          boardFromSquare : oneMove.from,
                          boardToSquare : oneMove.to, 
                          san : oneMove.san, 
                          movePercentage : oneMove.movePercentage,
                          comment : oneMove.comment }
    moveHistoryStack.value.push(oneMoveHistory)
    moveHistoryTrigger.value++

    const keyParts = oneMove.toCombined.split("~")

    // if this is a new move for the repertoire add it, reload repertoire and then get available moves
    if (keyParts[0] > repertoireHighestRecordIndex && repertoireLocked == false)
      updateRepertoire(oneMove)
    else {
      updateTreeView()
      getAvailableMoves(oneMove.toCombined,oneMove.movePercentage)
    }
  }

  function processAvailableMovesJsonResponse (json) {
    //console.log("*** in process json callback",json)
    if (json.hasOwnProperty('error')) {
      openErrorModal(json['programRun'],json['error'])
      return;
    }

    const whiteString = json['white']
    const blackString = json['black']
    const whites = whiteString.split("\t")
    const blacks = blackString.split("\t")
    const sans = json['san'].split("\t")
    const move_percentages = json['percentage'].split("\t")
    const pretty_game_counts = json['pretty'].split("\t")

    // if was a recalc then update the movehistory stack
    // const recalc_percentages_string = String(json['recalc_percentages'])
    // if (recalc_percentages_string.length > 0) {
    //   const recalc_percentages = recalc_percentages_string.split("\t")
    //   for (var i = 0; i < recalc_percentages.length; i++)
    //     moveHistoryStack.value[i].movePercentage = recalc_percentages[i]
    // }

    const parent_unique = json['parent_unique']
    const from_fenid4 = json['from_fenid4']
    const fromCombined = parent_unique + "~" + from_fenid4
    const fens = json['fen'].split("\t")
    const fenid4s = json['fenid4'].split("\t")
    const froms = json['from'].split("\t")
    const tos = json['to'].split("\t")
    const inRange = json['in_range'].split("\t")

    const count = json['count']

    var scores = []
    const weights = [ -6, -6, -5, -4, -3, -2, -1, 0, 1, 2, 3, 4, 5, 6, 6]

    eighty.value = 100
    
    scores = json['scores'].split("\t")

    availableMoves.value = []

    for (var i=0; i < count; i++) {

      // see if move is in tree, if so grab the "to" index and add to fenid
      //     if not, use the "next" move index

      var moveInTree = false
      var toCombined = (repertoireHighestRecordIndex + 1) + "~" + fenid4s[i]
      const treeMoves = repTree.get(fromCombined)
      if (treeMoves != null)
          for (var j=0; j < treeMoves.length; j++) {
            const keyParts = treeMoves[j].toCombined.split("~")
            if (keyParts[1] == fenid4s[i]) {
              toCombined = treeMoves[j].toCombined
              moveInTree = true
              break
            }
          }

      const whiteNumber = Number(whites[i])
      const blackNumber = Number(blacks[i])
      const totalNumber = whiteNumber + blackNumber

      const whitePercent = (totalNumber > 0) ? whiteNumber * 100 / totalNumber : 0
      const blackPercent = (totalNumber > 0) ? blackNumber * 100 / totalNumber : 0
      const whitePercentString = whitePercent.toFixed(1)
      const blackPercentString = blackPercent.toFixed(1)

      // build the bar chart info
      var moves = []
      var weightedScores = []
      var isSelected = false

      const moveScores = scores[i].split(",")
      for (var moveIndex = 0; moveIndex < 5; moveIndex++) {
        const offset = moveIndex * 15
        var weight = 0
        var percentages = []
        for (var percentIndex = 0; percentIndex < 15; percentIndex++) {
          const oneNumber = Number(moveScores[offset + percentIndex])
          weight += oneNumber * weights[percentIndex]
          percentages.push(oneNumber)
        }
        moves.push(percentages)
        weightedScores.push((weight/100).toFixed(2))
      }

      // if there is a saved selected list, use it, otherwise use the first two
      if (saveSelectedSans != null && saveSelectedSans.length) {
        var isSelected = false
        for (var j=0; j < saveSelectedSans.length; j++)
          if (sans[i] == saveSelectedSans[j]) {
            isSelected = true
            break
          }
      }
      else
        isSelected = (i < 2)

      const percentage = Number(move_percentages[i])

      var textType = "needed"
      var playerMoveInTree = false

      if (repertoireId == "0")
        textType = "player"
      else
      if (1 - (moveHistoryStack.value.length % 2) == playingWhite) {
        textType = "player"
        playerMoveInTree = moveInTree
      }
      else
      if (percentage < repertoirePercentage)
        textType = "ignore"
      else {
        // see if move in reptree
        if (moveInTree)
          textType = "done"
      }

      const displayPercentage = (inRange[i] != "1") ? "all" : percentage.toFixed(2) + "%"
      const oneMove = { index: i, 
                        fromCombined : fromCombined,
                        toCombined: toCombined,
                        white : whitePercentString, 
                        black : blackPercentString, 

                        highlightPlayerMove : playerMoveInTree,

                        // Slice & Dice
                        weightedScores : weightedScores,
                        selected : isSelected,
                        moves : moves,

                        from : froms[i], to: tos[i],
                        countDisplay : pretty_game_counts[i],
                        textType : textType,
                        san : sans[i], 
                        percentDisplay : displayPercentage,
                        movePercentage : move_percentages[i],
                        fen : fens[i]
                      }
          
      availableMoves.value.push(oneMove)
    }
    availableMovesTrigger.value++

    // update the toolbar
    const ply = moveHistoryStack.value.length
    setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_BACK,(ply >= 1) ? false : true)
    setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_REWIND,(ply >= 1) ? false : true)
    setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_COMMENT,(!repertoireLocked && ply >= 1) ? false : true)

    toolbarTrigger.value++
    
    // send the move to the repertoire tree.  
    // (note: this can be short circuited by seeing if the move is already in the tree)

  }

  function debounce(callback, interval=500) {
    let debounceTimeoutId;

    return function(...args) {
      clearTimeout(debounceTimeoutId);
      debounceTimeoutId = setTimeout(() => callback.apply(this, args), interval);
    };
  }

  function getAvailableMoves(fenidCombined,previousMovePercentage) {
    const keyParts = fenidCombined.split("~")

    var formData = {
      parent_unique : keyParts[0],
      fenid4 : keyParts[1],
      previous_percent : previousMovePercentage,
      white_to_move : 1 - (moveHistoryStack.value.length % 2),
      playing_white : playingWhite,

      min_rating : repertoireRatingMin,
      max_rating : repertoireRatingMax - 1,
      recalc_fenid4s : ""    }


    // zap waiting for next move values
    availableMoves.value = []
    setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_BACK,true)
    setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_REWIND,true)
    if (!repertoireLocked)
      setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_COMMENT,true)
    
    eighty.value = -2

    //console.log("before post for availablemoves -> ",formData)

    useGetData("opening_explorer_move4.php", formData, processAvailableMovesJsonResponse)
  }

  function processUpdateTreeJsonResponse(json) {
    //console.log("*** in update tree json callback",json)
    if (json.hasOwnProperty('error')) {
      openErrorModal(json['programRun'],json['error'])
      return;
    }
    getCoverage()
  }

  function updateRepertoire(oneMove) {
    if (repertoireId == "0")
      return

    var formData = {
      from_fenid_combined : oneMove.fromCombined,
      to_fenid_combined : oneMove.toCombined,
      repid : repertoireId,
      white_to_move : 1 - (moveHistoryStack.value.length % 2),
      playing_white : playingWhite,

      san : oneMove.san,
      fen : oneMove.fen,
      board_from : oneMove.from,
      board_to : oneMove.to
    }

    //console.log("before post for update repertoire -> ",formData)

    useGetData("opening_update_repertoire_move.php", formData, processUpdateTreeJsonResponse)

  }

  // this version recursively opens as many branches as the user has set to open
  function repTreeBranchClick(branchElement) {
    //console.log("**** in branch click",branchElement)
    const move = branchElement.move
    move.branchIsOpen = !move.branchIsOpen
    buildRecursiveDisplayTreeView(initialFenidCombined,!playingWhite,0,0)

  }

  function clearTreeDisplaySelectedFlag() {
    for (var i=0; i < treeDisplay.value.length; i++) {
      for (var j=0; j < treeDisplay.value[i].moves.length; j++)
        treeDisplay.value[i].moves[j].selected = false
    }
  }

  function repTreeMoveClick(branchElement) {
    //console.log("**** in move click",branchElement)

    clearTreeDisplaySelectedFlag()
  
    branchElement.selected = true
    treeTrigger.value++

    const move = branchElement.move

    const fenidCombines = move.fenidCombinedStackString.split(",")

    moveHistoryStack.value = []

    // check for initial board
    if (fenidCombines.length == 1) {
      initialBoardSelectedInTree = true
      fen.value = INITIAL_FEN
      getAvailableMoves(initialFenidCombined,100)
      moveHistoryTrigger.value++
      return
    }

    initialBoardSelectedInTree = false
    for (var i=0; i < fenidCombines.length - 1; i++) {
      const move = repTree.get(fenidCombines[i])
      if (move == null)
        break

      var oneMove = null
      for (var j=0; j < move.length; j++)
        if (move[j].toCombined == fenidCombines[i+1]) {
          fen.value = move[j].fen
          const san = move[j].san
          const boardToSquare = move[j].boardToSquare
          const boardFromSquare = move[j].boardFromSquare
          const percent = move[j].move_percentage
          oneMove = {fen : fen.value, 
                          fromCombined : move[j].fromCombined,
                          toCombined : move[j].toCombined, 
                          boardToSquare : boardToSquare,
                          boardFromSquare : boardFromSquare,
                          san : san, movePercentage : percent,
                          comment : move[j].comment }
          moveHistoryStack.value.push(oneMove)
          break;
        }
    }

    moveHistoryTrigger.value++
    if (oneMove == null)
      getAvailableMoves(initialFenidCombined,100)
    else
      getAvailableMoves(oneMove.toCombined,oneMove.movePercentage)
  }

  function findMoveInTreeRecursive(parentFenidCombined,moveFenidCombinedString) {
    const moves = repTree.get(parentFenidCombined)
    var found = false

    if (moves == null)
      return (false)

    // recurse on each move from the parent
    for (var i=0; i < moves.length; i++) {
        const toFenid4 = moves[i].to

        if (moves[i].fenidCombinedStackString == moveFenidCombinedString) {
          return (true)
        }
       
        const found = findMoveInTreeRecursive(moves[i].toCombined,moveFenidCombinedString)

        // open up all parent branches
        if (found) {
          if (moves[i].canBranchAfterThisMove)
            moves[i].branchIsOpen = true
          return (true)
        }
      }
    return (found)
  }

  function buildMoveHistoryFenidCombinedString() {
    var moveFenidCombineds = [ initialFenidCombined ]
    for (var i = 0; i < moveHistoryStack.value.length; i++)
      moveFenidCombineds.push(moveHistoryStack.value[i].toCombined)
    const moveFenidCombinedString = moveFenidCombineds.join(",")
    return (moveFenidCombinedString)
  }

  // find boardmove in repertoire tree
  function findMoveInTree() {

    // get the move stack to find the move in the tree
    const moveFenidCombinedString = buildMoveHistoryFenidCombinedString()
    //console.log("findMoveInTree " + moveFenidCombinedString)
    findMoveInTreeRecursive(initialFenidCombined,moveFenidCombinedString)
  }

   // is boardmove  already in the visible tree?
   //    if yes, set selected and return true.
   function findMoveInTreeDisplay() {

    // get the move stack to find the move in the tree
    const moveFenidCombinedString = buildMoveHistoryFenidCombinedString()
    //console.log("FIND IN TREE STRING " + moveFenidCombinedString)

    if (moveFenidCombinedString == initialFenidCombined) {
      initialBoardSelectedInTree = true
      if (treeDisplay.value.length > 0)
        treeDisplay.value[0].moves[0].selected = true
      return (false)
    }

    for (var i=0; i < treeDisplay.value.length; i++) {
      for (var j=0; j < treeDisplay.value[i].moves.length; j++) {
        if (treeDisplay.value[i].moves[j].move.fenidCombinedStackString == moveFenidCombinedString) {
          treeDisplay.value[i].moves[j].selected = true
          return (true)
        }
      }
    }
    return (false)
  }

  // build repertoire tree display. Allow for multiple open branches
  function buildRecursiveDisplayTreeView(parentFenidCombined,parentIsOpen,ply,indentLevel) {
    if (parentFenidCombined == initialFenidCombined) {
      treeDisplay.value = []
      treeDisplay.value.push({index:treeDisplay.value.length,indentLevel : indentLevel,moves : []})

      const oneMove = { 
                from : "0~0", 
                to : initialFenidCombined, 
                percent : 0, 
                move_percentage : 0,
                san : t("Init"), 
                white : 0,
                count : 0,
                comment : "",
                fen : INITIAL_FEN,
                first_move_not_addressed : 0,
                fenidCombinedStackString : initialFenidCombined
              }

      const oneTreeDisplayMove = {
              index : 0,
              ply : -1,
              indentLevel : 0,
              isOpen : false,
              selected : initialBoardSelectedInTree,
              parentIsOpen : false,
              move : oneMove
      }
      treeDisplay.value[0].moves.push(oneTreeDisplayMove)

    }

    const moves = repTree.get(parentFenidCombined)
    if (moves == null)
      return

    // recurse on each move from the parent
    for (var i=0; i < moves.length; i++) {
      const toFenidCombined = moves[i].toCombined

      const oneTreeDisplayMove = {
              index : treeDisplay.value[treeDisplay.value.length-1].moves.length,
              ply : ply,
              indentLevel : indentLevel,
              isOpen : false,
              selected : false,
              parentIsOpen : parentIsOpen,
              move : moves[i]
      }
      if (parentIsOpen) {
        treeDisplay.value.push({index:treeDisplay.value.length,indentLevel : indentLevel+1,moves : []})
      }
      if (parentIsOpen || i == 0) {
        treeDisplay.value[treeDisplay.value.length-1].moves.push(oneTreeDisplayMove)
        buildRecursiveDisplayTreeView(moves[i].toCombined,moves[i].branchIsOpen,ply+1,
                        (parentIsOpen) ? indentLevel+1: indentLevel)
      }
    }

    if (parentFenidCombined == initialFenidCombined) {
      treeTrigger.value++
    }
  }

  // walk entire repertoire building the list of "combined" fenid4s to get to this move
  function buildRepTreeFenidStack(parentFenidCombined,fenidCombinedStackString) {

    const moves = repTree.get(parentFenidCombined)
    if (moves == null)
      return

    const nextMoves = (moves.length != 1) ? null : repTree.get(moves[0].toCombined)
    const canBranch = (nextMoves != null && nextMoves.length > 1)

    // recurse on each move from the parent
    for (var i=0; i < moves.length; i++) {
      const toCombined = moves[i].toCombined
      moves[i].canBranchAfterThisMove = canBranch
      moves[i].branchIsOpen = false
      moves[i].fenidCombinedStackString = fenidCombinedStackString + "," + moves[i].toCombined
      buildRepTreeFenidStack(moves[i].toCombined,moves[i].fenidCombinedStackString)
    }
  }

  function updateTreeView() {
    // clear the previous selection and see if the new move is already visible
    clearTreeDisplaySelectedFlag()

    const moveIsVisible = findMoveInTreeDisplay()

    // move isn't visible so find it in repertoire and open up parents
    if (moveIsVisible == false) {
      findMoveInTree()
      buildRecursiveDisplayTreeView(initialFenidCombined,!playingWhite,0,0 )
      findMoveInTreeDisplay()
    }
    treeTrigger.value++
  }

  function processCoverageJsonResponse (json) {
    //console.log(" in coverage json callback",json)
    if (json.hasOwnProperty('error')) {
      openErrorModal(json['programRun'],json['error'])
      return;
    }

    repertoireHighestRecordIndex = Number(json['highest'])

    const froms = json['froms'].split(",")
    const tos = json['tos'].split(",")
    const board_froms = json['board_froms'].split(",")
    const board_tos = json['board_tos'].split(",")
    const percents = json['percents'].split(",")
    const sans = json['sans'].split(",")
    const whites = json['whites'].split(",")
    const counts = json['counts'].split(",")
    const fens = json['fens'].split(",")
    const first_move_not_addresseds = json['first_move_not_addressed'].split(",")
    const move_percentages = json['move_percentage'].split(",")
    const comments = json['comments'].split("\t")

    const count = Number(json['count'])

    repTree.clear();

    for (var i=0; i < count; i++) {

      const oneTreeMove = { 
                fromCombined : froms[i], 
                toCombined : tos[i], 
                boardFromSquare : board_froms[i],
                boardToSquare : board_tos[i],
                percent : Number(percents[i]), 
                move_percentage : move_percentages[i],
                san : sans[i], 
                comment : comments[i],
                white : whites[i],
                count : counts[i],
                fen : fens[i],
                first_move_not_addressed : parseInt(first_move_not_addresseds[i]),
                fenidCombinedStackString : ""
              }
  
      if (repTree.has(froms[i]) == false)
        repTree.set(froms[i],[])
      const toArray = repTree.get(froms[i])
      toArray.push(oneTreeMove)
    }

    buildRepTreeFenidStack(initialFenidCombined,initialFenidCombined)

    updateTreeView()

    if (moveHistoryStack.value.length > 0) {
      const oneMoveHistory = moveHistoryStack.value[moveHistoryStack.value.length - 1]
      //fen.value = oneMoveHistory.fen
      getAvailableMoves(oneMoveHistory.toCombined,oneMoveHistory.movePercentage)
    }
    else
      getAvailableMoves(initialFenidCombined,100)
  }

  function getCoverage() {

    if (repertoireId == "0")
      return

    var formData = {
      playing_white : playingWhite,
      repid : repertoireId,
      min_rating : repertoireRatingMin,
      max_rating : repertoireRatingMax - 1,
      percentage : repertoirePercentage
    }

    //console.log("coverage postdata ",formData)
    useGetData("opening_coverage2.php", formData, processCoverageJsonResponse)
    
  }

  function processRepertoireInfoJsonResponse (json) {
    //console.log(" in initial json callback",json)
    if (json.hasOwnProperty('error')) {
      openErrorModal(json['programRun'],json['error'])
      return;
    }

    repertoireId = json['repid']
    if (repertoireId != 0) {
      repertoireRatingMax = Number(json['rating_max'])
      repertoireRatingMin = Number(json['rating_min'])
      repertoirePercentage = json['percentage']
      repertoireLocked = (json['locked'] == 1)
      repertoireName = json['name']

      playingWhite = parseInt(json['playing_white'])
      if (playingWhite == 0)
        orientation.value = "black"

      repertoireDescription.value = repertoireName + ", " + t("Rating") + ":" + repertoireRatingMin + "-" +
                repertoireRatingMax + " " + t("with") + " " + repertoirePercentage + "% " + t("Filter")
      if (repertoireLocked)
        repertoireDescription.value += " ** Locked **"
    }
    else
      repertoireDescription.value = "***** Use the Repertoire Manager to Select Repertoire ******"


    // TESTING. NO REPERTOIRE
    //repertoireId = "0"

    setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_DISPLAY_TOGGLE,
              (repertoireId == "0") ? true : false)
    setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_COPY,
              (repertoireId == "0") ? true : false)
    toolbarTrigger.value++

    getCoverage()
    menuTrigger.value++
  }

  function getRepertoireInfo() {
    var formData = { }
    useGetData("opening_repertoire_info.php", formData, processRepertoireInfoJsonResponse)
  }

  function processSetCommentJsonResponse (json) {
    //console.log(" in update comment callback",json)
    if (json.hasOwnProperty('error')) {
      openErrorModal(json['programRun'],json['error'])
      return;
    }
  }

  function setComment(oneMove,comment) {
    if (repertoireId == "0")
      return

    var formData = {
      from_fenid_combined : oneMove.fromCombined,
      to_fenid_combined : oneMove.toCombined,
      repid : repertoireId,
      comment : comment
    }

    //console.log("before post for update comment -> ",formData)

    useGetData("opening_update_comment.php", formData, processSetCommentJsonResponse)
  }

  function processgrabPGNJsonResponse (json) {
    //console.log(" in PGN JSON callback",json)
    if (json.hasOwnProperty('error')) {
      openErrorModal(json['programRun'],json['error'])
      return;
    }
    const pgn = json['pgn']
    navigator.clipboard.writeText(pgn)
  }

  function grabPGN() {
    if (repertoireId == "0")
      return

    const timeNow = Math.floor(Date.now() / 1000)

    const formData = {
      time : timeNow,
      onerep : repertoireId
    }

    //console.log("before post  -> ",formData)

    useGetData("opening_export2.php", formData, processgrabPGNJsonResponse)
  }

</script>

<style scoped>

.move_slider_style {
  width : 50px;
}

ion-list {
    padding-top : 0px;
    padding-bottom : 0px;
  }

</style>
